import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Mail, Phone, Building2, BadgeCheck, Calendar, LogOut, Loader2, LocateFixed } from "lucide-react";
import { useTheme } from "../../contexts/ThemeContext";
import { toast } from "sonner";
import TechTapToPaySetup from "../../components/tech/TechTapToPaySetup";
import { Switch } from "../../components/ui/switch";
import useTechLocationSharing, { resetSharingState } from "../../hooks/useTechLocationSharing";
import LocationDisclosureDialog, {
  LOCATION_DISCLOSURE_KEY,
  hasAcceptedLocationDisclosure,
} from "../../components/tech/LocationDisclosureDialog";

const API = process.env.REACT_APP_BACKEND_URL;

const InfoRow = ({ icon: Icon, label, value, isDark, testid }) => (
  <div className={`flex items-center gap-3 px-4 py-3.5 ${isDark ? 'border-white/5' : 'border-gray-100'} border-b last:border-b-0`} data-testid={testid}>
    <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${isDark ? 'bg-white/5' : 'bg-gray-100'}`}>
      <Icon className="w-4 h-4 text-[#FFCC00]" />
    </div>
    <div className="min-w-0">
      <p className={`text-[11px] uppercase tracking-wide ${isDark ? 'text-white/40' : 'text-gray-400'}`}>{label}</p>
      <p className={`text-sm font-medium truncate ${isDark ? 'text-white/90' : 'text-gray-900'}`}>{value || "—"}</p>
    </div>
  </div>
);

export default function TechProfile() {
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const { enabled: sharingOn, loaded: sharingLoaded, setSharing } = useTechLocationSharing();
  const [togglingShare, setTogglingShare] = useState(false);
  const [showDisclosure, setShowDisclosure] = useState(false);

  const toggleSharing = async (on) => {
    // Policy-required disclosure shown before any system permission prompt
    if (on && !hasAcceptedLocationDisclosure()) {
      setShowDisclosure(true);
      return;
    }
    setTogglingShare(true);
    try {
      await setSharing(on);
      toast.success(on ? "Location sharing turned on" : "Location sharing turned off");
    } catch {
      toast.error("Couldn't update location sharing — check your connection and try again");
    }
    setTogglingShare(false);
  };

  const acceptDisclosure = async () => {
    localStorage.setItem(LOCATION_DISCLOSURE_KEY, "1");
    setShowDisclosure(false);
    await toggleSharing(true);
  };

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const token = localStorage.getItem("tech_token");
        const res = await axios.get(`${API}/api/tech/profile`, { headers: { Authorization: `Bearer ${token}` } });
        setProfile(res.data);
      } catch {
        const cached = localStorage.getItem("tech_user");
        if (cached) { try { setProfile(JSON.parse(cached)); } catch { /* ignore */ } }
      }
      setLoading(false);
    };
    fetchProfile();
  }, []);

  const handleLogout = () => {
    resetSharingState();
    localStorage.removeItem("tech_token");
    localStorage.removeItem("tech_user");
    toast.success("Logged out successfully");
    navigate("/tech/login");
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[#FFCC00]" /></div>;

  const memberSince = profile?.member_since
    ? new Date(profile.member_since).toLocaleDateString(undefined, { year: "numeric", month: "long" })
    : null;

  return (
    <div className="max-w-xl mx-auto space-y-4" data-testid="tech-profile-page">
      {/* Identity card */}
      <div className={`rounded-2xl p-5 text-center ${isDark ? 'bg-[#0c0c14] border border-white/5' : 'bg-white border border-gray-200'}`}>
        <div className="w-16 h-16 mx-auto rounded-2xl flex items-center justify-center text-2xl font-bold text-black mb-3"
          style={{ background: 'linear-gradient(135deg, #FFCC00 0%, #E6B800 100%)' }}>
          {profile?.name?.charAt(0)?.toUpperCase() || "T"}
        </div>
        <h2 className={`text-lg font-bold font-['Manrope'] ${isDark ? 'text-white' : 'text-gray-900'}`} data-testid="tech-profile-name">{profile?.name || "Technician"}</h2>
        <p className="inline-flex items-center gap-1 mt-1 text-xs font-semibold text-[#FFCC00]">
          <BadgeCheck className="w-3.5 h-3.5" /> Field Technician
        </p>
      </div>

      {/* Details */}
      <div className={`rounded-2xl overflow-hidden ${isDark ? 'bg-[#0c0c14] border border-white/5' : 'bg-white border border-gray-200'}`}>
        <InfoRow icon={Mail} label="Email" value={profile?.email} isDark={isDark} testid="tech-profile-email" />
        <InfoRow icon={Phone} label="Phone" value={profile?.phone} isDark={isDark} testid="tech-profile-phone" />
        <InfoRow icon={Building2} label="Company" value={profile?.business_name} isDark={isDark} testid="tech-profile-company" />
        {memberSince && <InfoRow icon={Calendar} label="Member Since" value={memberSince} isDark={isDark} testid="tech-profile-since" />}
      </div>

      {/* Location sharing */}
      <div className={`rounded-2xl overflow-hidden ${isDark ? 'bg-[#0c0c14] border border-white/5' : 'bg-white border border-gray-200'}`} data-testid="tech-profile-location-sharing">
        <LocationDisclosureDialog
          open={showDisclosure}
          onAccept={acceptDisclosure}
          onCancel={() => setShowDisclosure(false)}
        />
        <div className="flex items-center gap-3 px-4 py-3.5">
          <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${sharingOn ? 'bg-green-500/10' : isDark ? 'bg-white/5' : 'bg-gray-100'}`}>
            <LocateFixed className={`w-4 h-4 ${sharingOn ? 'text-green-500' : 'text-[#FFCC00]'}`} />
          </div>
          <div className="min-w-0 flex-1">
            <p className={`text-sm font-medium ${isDark ? 'text-white/90' : 'text-gray-900'}`}>Location Sharing</p>
            <p className={`text-[11px] ${isDark ? 'text-white/40' : 'text-gray-400'}`}>
              {sharingOn
                ? "Dispatch can see your latest location while you're on duty"
                : "Your location is not shared with dispatch"}
            </p>
          </div>
          <Switch
            checked={sharingOn}
            disabled={!sharingLoaded || togglingShare}
            onCheckedChange={toggleSharing}
            data-testid="tech-profile-sharing-switch"
          />
        </div>
      </div>

      {/* Stage 2 — Tap to Pay device onboarding (mobile app only) */}
      <TechTapToPaySetup isDark={isDark} />

      {/* Sign out */}
      <button
        onClick={handleLogout}
        data-testid="tech-profile-logout"
        className="w-full flex items-center justify-center gap-2 py-3.5 rounded-2xl text-sm font-bold text-red-500 border border-red-500/20 bg-red-500/5 active:scale-[0.99] transition-transform"
      >
        <LogOut className="w-4 h-4" /> Sign Out
      </button>
    </div>
  );
}
